import React, { useState, useEffect } from 'react';
import { FaClock } from "react-icons/fa6";
import { IoIosHelpCircle } from "react-icons/io";
import { motion } from 'framer-motion';
import * as Dialog from '@radix-ui/react-dialog';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaCoffee } from "react-icons/fa";
import { AnimatePresence } from 'framer-motion';
import { getTodaysPuzzle, generateLetterGrid } from './puzzles';
import confetti from 'canvas-confetti';


const AnimatedCoffeeButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 15px;
  position: absolute;
  top: 0;
  right: 0;
`;


const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.5 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 10,
    },
  },
};

const gridSize = 5;

const WordGameContainer = styled.div`
  height: 100vh;
  padding: 16px;
  overflow: hidden;
  background-color: #f5f5f5;
`;

const Title = styled.h1`
  color: #333;
  margin-bottom: 4px;
  text-align: center;
  width: 100%;
`;

const HeaderRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
`;

const DateText = styled.p`
  margin: 0 0 16px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
`;

const HelpGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 22px);
  grid-template-rows: repeat(5, 22px);
  gap: 6px;
  margin: 10px 0 6px 0;
  justify-content: center;
`;

const HelpCell = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background: ${props => props.isCurrent ? '#e0106e' : props.isOption ? '#b0c4de' : '#eee'};
  border: 1px solid ${props => props.isCurrent ? '#c40d5e' : props.isOption ? '#9db2c8' : '#ddd'};
`;

const DialogButton = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  background-color: #541930;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  margin-top: 0px;
  fontSize: 14px,
  font-weight: 500;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;
  outline: none;
`;

const LetterCard = styled(motion.div)`
  width: 60px;
  height: 60px;
  position: relative;
  perspective: 1000px;
  cursor: ${props => props.isFound ? 'default' : 'pointer'};
  pointer-events: ${props => props.isFound ? 'none' : 'auto'};
`;

const CardInner = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  text-align: center;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${props => props.isFound ? 'rotateY(180deg)' : 'rotateY(0deg)'};
`;

const CardFront = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => 
  props.isSelected ? '#e0106e' : 
  props.isOption ? '#6310e0' : 
  '#541930'};
  color: white;
  font-size: 20px;
  font-weight: bold;
`;

const CardBack = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #707c7d;
  color: white;
  font-size: 20px;
  font-weight: bold;
  transform: rotateY(180deg);
`;

const TileNumber = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  font-size: 12px;
  font-weight: bold;
  color: white;
  background-color: rgba(0, 0, 0, 0.4);
  border-radius: 3px;
  padding: 2px 4px;
  line-height: 1;
`;

function WordGame() {
  // Initialize puzzle data in state
  const [puzzleData] = useState(() => {
    const todaysPuzzle = getTodaysPuzzle();
    return {
      knightTour: todaysPuzzle.knightTour,
      words: todaysPuzzle.words,
      theme: todaysPuzzle.theme,
      letters: generateLetterGrid(todaysPuzzle.knightTour, todaysPuzzle.words)
    };
  });

  const { knightTour, words, theme, letters } = puzzleData;

  const [selectedIndices, setSelectedIndices] = useState([]);
  const [selectedWord, setSelectedWord] = useState('');
  const [foundIndices, setFoundIndices] = useState([]);
  const [foundWords, setFoundWords] = useState([]);
  const [matchFlash, setMatchFlash] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [hasMounted, setHasMounted] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [isCompletionOpen, setIsCompletionOpen] = useState(false);
  const [shake, setShake] = useState(false);
  const [possibleMoves, setPossibleMoves] = useState([]);
  const [notification, setNotification] = useState('');
  const [showAnswers, setShowAnswers] = useState(false);

  // Force overflow hidden on this page
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setShake(true);
      setTimeout(() => setShake(false), 800);
    }, 20000);

    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    const timer = setInterval(() => {
      if (timerActive) {
        setElapsedTime((prev) => prev + 1);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [timerActive]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('view') === 'results') {
      setFoundIndices(Array.from({ length: 25 }, (_, i) => i));
      setFoundWords(words);
      setTimerActive(false);
      setShowAnswers(true);
    }
  }, [words]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCompletionMessage = (seconds) => {
    const mins = Math.floor(seconds / 60);
    
    if (seconds < 60) {
      return "⚡ Lightning fast! ⚡";
    } else if (mins >= 1 && mins < 3) {
      return "🚀 Impressive speed! 🚀";
    } else if (mins >= 3 && mins <= 5) {
      return "🎯 Nice work! 🎯";
    } else {
      return "🌟 You did it! 🌟";
    }
  };

  const isInKnightStepOrder = (indices) => {
    const steps = indices.map(i => knightTour[i]);
    for (let i = 1; i < steps.length; i++) {
      if (steps[i] !== steps[i - 1] + 1) return false;
    }
    return true;
  };

  const getKnightMoves = (index) => {
    const row = Math.floor(index / gridSize);
    const col = index % gridSize;
    const deltas = [
      [2, 1], [2, -1],
      [-2, 1], [-2, -1],
      [1, 2], [1, -2],
      [-1, 2], [-1, -2],
    ];

    return deltas
      .map(([dr, dc]) => {
        const r = row + dr;
        const c = col + dc;
        if (r >= 0 && r < gridSize && c >= 0 && c < gridSize) {
          return r * gridSize + c;
        }
        return null;
      })
      .filter(i => i !== null);
  };

  const clearSelection = () => {
    setSelectedIndices([]);
    setSelectedWord('');
    setPossibleMoves([]);
  };

  const handleHint = () => {
    const remainingWord = words.find(word => !foundWords.includes(word));
    if (!remainingWord) return;

    const wordIndex = words.indexOf(remainingWord);
    let stepOffset = 0;
    for (let i = 0; i < wordIndex; i++) {
      stepOffset += words[i].length;
    }

    const firstLetterIndex = knightTour.findIndex(step => step === stepOffset);
    if (firstLetterIndex === -1) return;

    clearSelection();
    setSelectedIndices([firstLetterIndex]);
    setSelectedWord(letters[firstLetterIndex]);
    setPossibleMoves(getKnightMoves(firstLetterIndex));
  };

  const toggleLetter = (index) => {
    if (foundIndices.includes(index)) return;

    // If there are selected letters, only allow clicking on possible moves or deselecting
    if (selectedIndices.length > 0) {
      const isDeselecting = selectedIndices.includes(index);
      const isValidMove = possibleMoves.includes(index);
      
      if (!isDeselecting && !isValidMove) {
        return; // Don't allow clicking invalid moves
      }
    }

    let updated = [];
    if (selectedIndices.includes(index)) {
      updated = selectedIndices.filter(i => i !== index);
    } else {
      updated = [...selectedIndices, index];
    }

    const updatedWord = updated.map(i => letters[i]).join('');
    setSelectedIndices(updated);
    setSelectedWord(updatedWord);

    const last = updated[updated.length - 1];
    setPossibleMoves(last !== undefined ? getKnightMoves(last) : []);

    if (
      words.includes(updatedWord) &&
      isInKnightStepOrder(updated) &&
      !foundWords.includes(updatedWord)
    ) {
      setFoundIndices(prev => [...prev, ...updated]);
      setFoundWords(prev => [...prev, updatedWord]);
      setSelectedIndices([]);
      setSelectedWord('');
      setPossibleMoves([]);
      setMatchFlash(true);
      setTimeout(() => setMatchFlash(false), 600);
    } 
    else if (words.includes(updatedWord) && !isInKnightStepOrder(updated)) {
      setNotification('Word found, but the tile order is incorrect.');
      setTimeout(() => setNotification(''), 2500);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  const allWordsFound = foundWords.length === words.length;

  useEffect(() => {
    if (allWordsFound && timerActive) {
      setIsCompletionOpen(true);
      setTimerActive(false);
      localStorage.setItem('wordGameCompleted', 'true');
      
      // Trigger confetti animation
      const duration = 3000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 0 };

      function randomInRange(min, max) {
        return Math.random() * (max - min) + min;
      }

      const interval = setInterval(function() {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return clearInterval(interval);
        }

        const particleCount = 50 * (timeLeft / duration);
        
        // Launch confetti from multiple positions
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
        });
        confetti({
          ...defaults,
          particleCount,
          origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
        });
      }, 250);
    }
  }, [allWordsFound, timerActive]);

  useEffect(() => {
    if (isSupportOpen || isHelpOpen) {
      setTimerActive(false);
    } else if (!allWordsFound) {
      setTimerActive(true);
    }
  }, [isSupportOpen, isHelpOpen, allWordsFound]);

  return (
    <WordGameContainer>
      <AnimatePresence>
        {notification && (
          <motion.div
            key="notification"
            initial={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
            animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
            exit={{ opacity: 0, scale: 0.8, x: '-50%', y: '-50%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              backgroundColor: '#ffdddd',
              color: '#8b0000',
              padding: '16px 24px',
              borderRadius: '12px',
              boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
              fontWeight: 500,
              fontSize: '16px',
              zIndex: 3000,
              textAlign: 'center',
              maxWidth: '200px',
              width: 'fit-content',
              whiteSpace: 'normal',
            }}
          >
            {notification}
          </motion.div>
        )}
      </AnimatePresence>

      <HeaderRow>
        <Title>Coffee Break</Title>
        <AnimatedCoffeeButton
          onClick={() => window.open('https://buymeacoffee.com/coffeebreakgames', '_blank')}
          animate={shake ? { rotate: [0, -15, 15, -10, 10, 0] } : { rotate: 0 }}
          transition={{ duration: 0.8, ease: "easeInOut" }}
          title="Buy me a coffee"
        >
          <FaCoffee />
        </AnimatedCoffeeButton>
      </HeaderRow>

      <DateText>{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</DateText>

      <Dialog.Root open={isCompletionOpen} onOpenChange={setIsCompletionOpen}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            width: '340px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '16px', color: '#2c3e50' }}>
              <FaClock /> {formatTime(elapsedTime)}
            </div>
            <span onClick={() => setIsHelpOpen(true)} style={{ fontSize: '24px', color: '#2c3e50', cursor: 'pointer' }} title="How to play">
              <IoIosHelpCircle />
            </span>
          </div>

          <h1 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '4px' }}>
            Today's Theme: {theme}
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '0px', marginBottom: '10px' }}>
            <h2 style={{ fontSize: '15px', fontWeight: 'normal', margin: 0 }}>
              ({words.length - foundWords.length} words left to find)
            </h2>
            <button
              onClick={handleHint}
              disabled={foundWords.length === words.length}
              style={{
                padding: '4px 10px',
                fontSize: '13px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: foundWords.length === words.length ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s',
                opacity: foundWords.length === words.length ? 0.6 : 1
              }}
            >
              Hint
            </button>
          </div>

          <motion.div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${gridSize}, 60px)`,
              gap: '10px',
            }}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {letters.map((letter, index) => {
              const isSelected = selectedIndices.includes(index);
              const isFound = foundIndices.includes(index);
              const isOption = possibleMoves.includes(index);
              const isClickable = isFound ? false : (selectedIndices.length === 0 || isSelected || isOption);
              const tileNumber = showAnswers ? (knightTour[index] + 1) : null;

              return (
                <LetterCard
                  key={index}
                  onClick={() => toggleLetter(index)}
                  isFound={isFound}
                  variants={itemVariants}
                  style={{ cursor: isClickable ? 'pointer' : 'not-allowed' }}
                >
                  <CardInner isFound={isFound}>
                    <CardFront isSelected={isSelected} isOption={isOption}>
                      {letter}
                      {tileNumber !== null && <TileNumber>{tileNumber}</TileNumber>}
                    </CardFront>
                    <CardBack>
                      {letter}
                      {tileNumber !== null && <TileNumber>{tileNumber}</TileNumber>}
                    </CardBack>
                  </CardInner>
                </LetterCard>
              );
            })}
          </motion.div>

          <div style={{ marginTop: '10px', fontSize: '16px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {selectedIndices.length > 0 && (
              <button
                onClick={clearSelection}
                style={{
                  padding: '4px 12px',
                  fontSize: '14px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#222'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#333'}
              >
                Clear
              </button>
            )}
            Selected: <strong>{selectedWord}</strong>
          </div>

          <motion.div
            animate={hasMounted ? {
              backgroundColor: matchFlash ? '#f9f9f9' : '#f9f9f9',
              opacity: 1,
            } : {}}
            transition={{
              duration: 0.4,
              ease: 'easeOut'
            }}
            style={{
              marginTop: '10px',
              width: '315px',
              textAlign: 'left',
              border: '1px solid #ccc',
              padding: '12px',
              borderRadius: '8px',
              color: '#2c3e50',
              fontSize: '16px',
              backgroundColor: '#f9f9f9'
            }}
          >
            <strong>Words found:</strong>{' '}
            {foundWords.length > 0 ? foundWords.join(', ') : 'None'}
          </motion.div>

          <Dialog.Portal>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <Dialog.Overlay style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                backdropFilter: 'blur(6px)',
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <motion.div
                  initial={{ opacity: 0, rotateY: 10 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  transition={{ duration: 0.2, ease: 'easeIn' }}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '32px 24px',
                    maxWidth: '320px',
                    width: '90%',
                    textAlign: 'center',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                  }}
                >
                  <h2 style={{ fontSize: '20px', marginBottom: '0px', marginTop: 0 }}>Finished in:</h2>
                  <div style={{ fontSize: '50px', fontWeight: 'bold', color: '#541930', marginBottom: '12px' }}>
                    {formatTime(elapsedTime)}
                  </div>
                  <p style={{ fontSize: '16px', color: '#000', marginBottom: '24px', marginTop: 0 }}>
                    {getCompletionMessage(elapsedTime)}
                  </p>
                  <DialogButton as="button" onClick={() => {
                    setIsCompletionOpen(false);
                    setShowAnswers(true);
                  }}>See answers</DialogButton>                  
                  <a
                    href="https://buymeacoffee.com/coffeebreakgames"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'block',
                      marginTop: '12px',
                      color: 'black',
                      textDecoration: 'bold',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                  Support this game
                  </a>
                </motion.div>
              </Dialog.Overlay>
            </motion.div>
          </Dialog.Portal>
        </div>
      </Dialog.Root>

      <Dialog.Root open={isHelpOpen} onOpenChange={setIsHelpOpen}>
        <Dialog.Portal>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Dialog.Overlay style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(6px)',
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <motion.div
                initial={{ opacity: 0, rotateY: 10 }}
                animate={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.2, ease: 'easeIn' }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '420px',
                  width: '90%',
                  textAlign: 'left',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
              >
                <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>How to Play</h2>
                <div style={{ color: '#555', lineHeight: 1.6 }}>
                  <p style={{ marginTop: 0 }}>Select letters on the grid to form words.</p>
                  <ul style={{ paddingLeft: '18px', marginTop: '6px' }}>
                    <li>The words must match today's theme.</li>
                    <li>Letters of a found word flip to reveal completion.</li>
                    <li>Choose any letter to start — the next letter must be one a knight could jump to from there (L-shaped: two squares one way, one square perpendicular).</li>
                  </ul>
                  {(() => {
                    const previewGridSize = 5;
                    const currentIndex = 12;
                    const row = Math.floor(currentIndex / previewGridSize);
                    const col = currentIndex % previewGridSize;
                    const deltas = [
                      [2, 1], [2, -1], [-2, 1], [-2, -1],
                      [1, 2], [1, -2], [-1, 2], [-1, -2]
                    ];
                    const options = new Set();
                    deltas.forEach(([dr, dc]) => {
                      const r = row + dr;
                      const c = col + dc;
                      if (r >= 0 && r < previewGridSize && c >= 0 && c < previewGridSize) {
                        options.add(r * previewGridSize + c);
                      }
                    });
                    return (
                      <div>
                        <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>Current position and knight move options:</div>
                        <HelpGrid>
                          {Array.from({ length: previewGridSize * previewGridSize }, (_, i) => (
                            <HelpCell
                              key={i}
                              isCurrent={i === currentIndex}
                              isOption={options.has(i)}
                            />
                          ))}
                        </HelpGrid>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 14, height: 14, borderRadius: 3, background: '#e0106e', border: '1px solid #c40d5e' }} />
                            <span style={{ fontSize: 12, color: '#666' }}>Current position</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{ width: 14, height: 14, borderRadius: 3, background: '#b0c4de', border: '1px solid #9db2c8' }} />
                            <span style={{ fontSize: 12, color: '#666' }}>Possible next moves</span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => setIsHelpOpen(false)} style={{ marginTop: '12px', background: '#eee', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer' }}>Got it, let's play! 😎</button>
                </div>
              </motion.div>
            </Dialog.Overlay>
          </motion.div>
        </Dialog.Portal>
      </Dialog.Root>

      <Dialog.Root open={isSupportOpen} onOpenChange={setIsSupportOpen}>
        <Dialog.Portal>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.2 }}>
            <Dialog.Overlay style={{
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              backdropFilter: 'blur(6px)',
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <motion.div
                initial={{ opacity: 0, rotateY: 10 }}
                animate={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.2, ease: 'easeIn' }}
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '24px',
                  maxWidth: '420px',
                  width: '90%',
                  textAlign: 'center',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}
              >
                <h2 style={{ fontSize: '22px', marginBottom: '12px' }}>Support My Work</h2>
                <p style={{ color: '#555', marginBottom: '16px' }}>
                  Enjoying the game? You can support me with a coffee!
                </p>

                <a
                  href="https://buymeacoffee.com/coffeebreakgames"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block',
                    padding: '12px 24px',
                    backgroundColor: '#FFDD00',
                    color: '#111',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    textDecoration: 'none',
                    fontSize: '16px',
                    marginBottom: '12px',
                    transition: 'background 0.2s',
                  }}
                >
                  ☕ Buy Me a Coffee
                </a>

                <div>
                  <button
                    onClick={() => setIsSupportOpen(false)}
                    style={{
                      marginTop: '12px',
                      background: '#eee',
                      border: 'none',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    No thanks, I like free things 😎
                  </button>
                </div>
              </motion.div>
            </Dialog.Overlay>
          </motion.div>
        </Dialog.Portal>
      </Dialog.Root>
    </WordGameContainer>
  );
}

export default WordGame;